import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useContext, useEffect, useState } from 'react';
import interpolateContent from '../../../helpers/interpolate-content';
import { SubAccountType } from '../../../models/subaccount-type';
import { UserManagementType } from '../../../models/user-management-type';
import subaccountDeactivate from '../../../services/customer-api/endpoints/subaccount/deactivate';
import subaccountGet from '../../../services/customer-api/endpoints/subaccount/get';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import AddNewUserForm from '../../molecules/add-new-user-form/add-new-user-form';
import AlertModal from '../../molecules/alert-modal/alert-modal';
import NoDataBlock from '../../molecules/no-data-block/no-data-block';
import RichText from '../../molecules/rich-text/rich-text';
import UsersTable from '../../molecules/users-table/users-table';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import { UserStateContext } from '../../providers/user/user.provider';
import resendRequest from '../../../services/customer-api/endpoints/subaccount/resend-request';

const UserManagement: FC<UserManagementType> = (props: UserManagementType) => {
  const {
    addNewUser,
    deactivatePopupCancelAction,
    deactivatePopupConfirmAction,
    deactivatePopupDescription,
    deactivatePopupHeading,
    heading,
    noUsersDescription,
    noUsersHeading,
    resendPopupDescription,
    resendPopupHeading,
    totalOfUsers,
  } = props;
  const [users, setUsers] = useState<SubAccountType[]>();
  const [showNewUserForm, setShowNewUserForm] = useState<boolean>(false);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const { ready } = useContext(UserStateContext);

  const getUsers = (): void => {
    subaccountGet().then((response: SubAccountType[]) => {
      setUsers(response);
    });
  };

  const deactivateUser = async (user: SubAccountType): Promise<void> => {
    setUsers(null);
    await subaccountDeactivate(user.userId);
    getUsers();
  };

  const openDeactivateAlertModal = (user: SubAccountType): void => {
    overlayDispatch(
      open(
        <AlertModal
          heading={deactivatePopupHeading}
          message={interpolateContent(
            deactivatePopupDescription,
            `<strong>${user.fullName}</strong>`
          )}
          cancelMessage={deactivatePopupCancelAction}
          onSubmit={() => deactivateUser(user)}
          submitMessage={deactivatePopupConfirmAction}
        />
      )
    );
  };

  const openResendAlertModal = async (user: SubAccountType): Promise<void> => {
    await resendRequest(user.email, '/subaccount/register/');

    overlayDispatch(
      open(
        <AlertModal
          heading={resendPopupHeading}
          message={resendPopupDescription}
        />
      )
    );
  };

  useEffect(() => {
    if (ready) {
      getUsers();
    }
  }, [ready]);

  if (!users) {
    return <LoadingSpinner className="h-96" />;
  }

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing">
      {!showNewUserForm && (
        <>
          <div className="flex flex-col md:flex-row justify-between">
            <div className="flex flex-col mb-8 md:mb-0">
              <h5 className="page-heading-five mb-8">{heading}</h5>
              <RichText className="text-grey-600">
                {interpolateContent(totalOfUsers, users?.length)}
              </RichText>
            </div>

            <ButtonPrimary
              className="mt-auto"
              name={addNewUser}
              onClick={() => {
                setShowNewUserForm(true);
              }}
            />
          </div>

          <AnimatePresence>
            {users && users.length !== 0 ? (
              <UsersTable
                onDeactivate={(user: SubAccountType) =>
                  openDeactivateAlertModal(user)
                }
                onResend={(user: SubAccountType) => openResendAlertModal(user)}
                {...props}
                users={users}
              />
            ) : (
              <motion.div
                animate={{ opacity: 1 }}
                className="py-component-spacing-mobile lg:py-component-spacing"
                initial={{ opacity: 0 }}
              >
                <NoDataBlock
                  heading={noUsersHeading}
                  message={noUsersDescription}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      <AnimatePresence>
        {showNewUserForm && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
            <AddNewUserForm
              {...props}
              onCancel={() => {
                setShowNewUserForm(false);
                getUsers();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;
